/**
 * Shared admin building blocks used by multiple editor modules.
 * Keep this file small — it is imported by every extracted editor.
 */
import React, { useState } from "react";
import { Loader2, Languages } from "lucide-react";
import { translateText } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

/** Multi-language input with an IT source-of-truth + one-click AI translation. */
export function MultiLangInput({ label, value, onChange, type = "input" }) {
  const langs = ["it", "en", "es", "fr", "de"];
  const [activeLang, setActiveLang] = useState("it");
  const [translating, setTranslating] = useState(false);
  const Field = type === "textarea" ? "textarea" : "input";

  const autoTranslate = async () => {
    const src = (value?.it || "").trim();
    if (!src) { toast({ title: "Inserisci prima il testo in italiano" }); return; }
    setTranslating(true);
    try {
      const trs = await translateText(src, "it", ["en","es","fr","de"]);
      onChange({ ...value, ...trs });
      toast({ title: "Tradotto in EN/ES/FR/DE" });
    } catch (e) {
      toast({ title: "Errore traduzione", description: e?.response?.data?.detail || e.message });
    } finally { setTranslating(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">{label}</label>
        <button
          type="button"
          onClick={autoTranslate}
          disabled={translating || !(value?.it || "").trim()}
          title="Traduci l'italiano nelle altre lingue con AI"
          className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-white border border-[#4a1c17] text-[#4a1c17] hover:bg-[#4a1c17] hover:text-[#f7ebdf] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {translating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
          {translating ? "Traduco…" : "Traduci con AI"}
        </button>
      </div>
      <div className="mt-1 flex gap-1 mb-1.5">
        {langs.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setActiveLang(l)}
            className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${
              activeLang === l ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-white border border-[#e8dcc8] text-[#7c5a4e]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <Field
        value={value?.[activeLang] || ""}
        onChange={(e) => onChange({ ...value, [activeLang]: e.target.value })}
        rows={type === "textarea" ? 2 : undefined}
        className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white resize-none"
      />
    </div>
  );
}
